import { useEffect, useMemo } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRightIcon } from "@heroicons/react/24/outline";
import LandingNavbar from "../components/landing/LandingNavbar";
import Footer from "../components/landing/Footer";
import { marketingOrigin } from "../data/seo/siteEntity";
import { COMPARISON_DISCLAIMER, COMPARISONS } from "../data/comparisons";
import { comMarketing, KW3_COMPARE, KW3_SYNTHFLOW_VAPI_ROWS } from "../data/seo/kw3OnPage";
import usePageSeo from "../hooks/usePageSeo";
import JsonLd from "../components/seo/JsonLd";
import { buildCollectionPageSchema } from "../data/seo/structuredData";

const SEO_TITLE = "FloGPT Comparisons — vs Vapi, Bland, Retell & Synthflow";
const SEO_DESCRIPTION =
    "Compare FloGPT with voice-AI platforms like Vapi, Bland, Retell, and Synthflow. FloGPT runs AI voice, web chat, and a seven-channel CRM inbox in one product — see how it stacks up.";
const SEO_KEYWORDS = [
    "FloGPT comparison",
    "FloGPT vs",
    "AI voice agent comparison",
    "Vapi alternative",
    "Bland alternative",
    "Retell alternative",
    "Synthflow alternative",
];

export default function ComparisonsIndex() {
    const kw3 = comMarketing();
    const title = kw3 ? KW3_COMPARE.title : SEO_TITLE;
    const description = kw3 ? KW3_COMPARE.description : SEO_DESCRIPTION;

    usePageSeo({
        title,
        description,
        keywords: SEO_KEYWORDS.join(", "),
        canonical: "/compare",
    });

    const collectionSchema = useMemo(
        () =>
            buildCollectionPageSchema({
                name: "FloGPT comparisons",
                description,
                path: "/compare",
                itemCount: COMPARISONS.length,
            }),
        [description],
    );

    const breadcrumbSchema = useMemo(
        () => ({
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            itemListElement: [
                { "@type": "ListItem", position: 1, name: "Home", item: `${marketingOrigin()}/` },
                { "@type": "ListItem", position: 2, name: "Compare", item: `${marketingOrigin()}/compare` },
            ],
        }),
        [],
    );

    const itemListSchema = useMemo(
        () => ({
            "@context": "https://schema.org",
            "@type": "ItemList",
            name: "FloGPT vs. competitors",
            numberOfItems: COMPARISONS.length,
            itemListElement: COMPARISONS.map((c, idx) => ({
                "@type": "ListItem",
                position: idx + 1,
                url: `${marketingOrigin()}/compare/${c.slug}`,
                name: `FloGPT vs ${c.competitor}`,
            })),
        }),
        [],
    );

    useEffect(() => {
        if (typeof window === "undefined") return;
        window.scrollTo({ top: 0, behavior: "auto" });
    }, []);

    return (
        <div className="bg-slate-900 min-h-screen overflow-x-hidden text-slate-100">
            <LandingNavbar />

            <main className="pt-20">
                <section className="relative overflow-hidden">
                    <div className="pointer-events-none absolute inset-0 -z-10">
                        <div className="absolute -left-32 top-10 h-72 w-72 rounded-full bg-orange-500/15 blur-3xl" />
                        <div className="absolute -right-32 bottom-10 h-72 w-72 rounded-full bg-violet-500/15 blur-3xl" />
                    </div>

                    <div className="mx-auto max-w-7xl px-6 lg:px-8 py-16 lg:py-24">
                        <motion.div
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.6 }}
                            className="max-w-3xl"
                        >
                            <div className="inline-flex items-center gap-2 rounded-full border border-slate-700/60 bg-slate-800/40 px-3 py-1 text-xs font-medium text-slate-300">
                                <span className="h-1.5 w-1.5 rounded-full bg-gradient-to-r from-orange-500 to-pink-500" />
                                Comparisons
                            </div>
                            <h1 className="mt-5 text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight text-white leading-[1.1]">
                                How FloGPT compares to{" "}
                                <span className="bg-gradient-to-r from-orange-500 to-pink-500 bg-clip-text text-transparent">
                                    voice-AI platforms
                                </span>
                            </h1>
                            <p className="mt-6 text-lg leading-relaxed text-slate-300">
                                Most voice-AI tools stop at the phone call. FloGPT runs the same AI across voice, web
                                chat, and seven messaging channels — with a built-in CRM inbox, a managed knowledge
                                base, and an outbound engine in one product. Pick a comparison below.
                            </p>
                            {kw3 ? (
                                <section className="mt-10" aria-labelledby="synthflow-vs-vapi">
                                    <h2 id="synthflow-vs-vapi" className="text-2xl font-bold text-white">
                                        {KW3_COMPARE.h2}
                                    </h2>
                                    <p className="mt-3 text-sm leading-relaxed text-slate-400">
                                        Both are voice-AI platforms. The cells below are the same facts as on{" "}
                                        <Link to="/compare/flogpt-vs-synthflow" className="font-medium text-orange-400 hover:text-orange-300">
                                            FloGPT vs Synthflow
                                        </Link>{" "}
                                        and{" "}
                                        <Link to="/compare/flogpt-vs-vapi" className="font-medium text-orange-400 hover:text-orange-300">
                                            FloGPT vs Vapi
                                        </Link>
                                        .
                                    </p>
                                    <div className="mt-4 overflow-x-auto rounded-2xl border border-white/10">
                                        <table className="w-full min-w-[36rem] text-left text-sm">
                                            <thead className="bg-white/5 text-slate-300">
                                                <tr>
                                                    <th scope="col" className="px-4 py-3 font-semibold"> </th>
                                                    <th scope="col" className="px-4 py-3 font-semibold">
                                                        <Link to="/compare/flogpt-vs-synthflow" className="text-orange-300 hover:text-orange-200">
                                                            Synthflow
                                                        </Link>
                                                    </th>
                                                    <th scope="col" className="px-4 py-3 font-semibold">
                                                        <Link to="/compare/flogpt-vs-vapi" className="text-orange-300 hover:text-orange-200">
                                                            Vapi
                                                        </Link>
                                                    </th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {KW3_SYNTHFLOW_VAPI_ROWS.map((row) => (
                                                    <tr key={row.label} className="border-t border-white/10 align-top">
                                                        <th scope="row" className="px-4 py-3 font-semibold text-white">{row.label}</th>
                                                        <td className="px-4 py-3 text-slate-300">{row.synthflow}</td>
                                                        <td className="px-4 py-3 text-slate-300">{row.vapi}</td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                </section>
                            ) : null}
                            <p className="mt-6 text-xs leading-relaxed text-slate-500">{COMPARISON_DISCLAIMER}</p>
                            <div className="mt-6">
                                <Link
                                    to="/alternatives"
                                    className="inline-flex items-center gap-1.5 text-sm font-semibold text-orange-400 hover:text-orange-300 transition-colors"
                                >
                                    Looking for alternatives by category?
                                    <ArrowRightIcon className="h-4 w-4" />
                                </Link>
                            </div>
                        </motion.div>
                    </div>
                </section>

                <section className="relative pb-24">
                    <div className="mx-auto max-w-7xl px-6 lg:px-8">
                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
                            {COMPARISONS.map((c, idx) => {
                                const Icon = c.Icon;
                                return (
                                    <motion.div
                                        key={c.slug}
                                        initial={{ opacity: 0, y: 20 }}
                                        animate={{ opacity: 1, y: 0 }}
                                        transition={{ duration: 0.4, delay: Math.min(idx * 0.06, 0.2) }}
                                    >
                                        <Link
                                            to={`/compare/${c.slug}`}
                                            className="group relative block h-full rounded-2xl border border-white/10 bg-white/5 backdrop-blur-sm p-6 hover:border-white/20 hover:bg-white/10 transition-all overflow-hidden"
                                        >
                                            <div className={`pointer-events-none absolute -inset-4 rounded-3xl blur-2xl opacity-0 group-hover:opacity-60 transition-opacity ${c.softAccent}`} />
                                            <div className="relative">
                                                <div className={`h-12 w-12 rounded-xl bg-gradient-to-br ${c.accent} flex items-center justify-center shadow-lg group-hover:scale-110 transition-transform`}>
                                                    <Icon className="h-6 w-6 text-white" />
                                                </div>
                                                <h2 className="mt-4 text-xl font-bold text-white group-hover:text-orange-400 transition-colors">
                                                    FloGPT vs {c.competitor}
                                                </h2>
                                                <div className="mt-1 text-[11px] font-mono text-slate-500">{c.competitorDomain}</div>
                                                <p className="mt-3 text-sm text-slate-400 leading-relaxed line-clamp-3">{c.tagline}</p>
                                                <div className="mt-5 inline-flex items-center gap-1.5 text-xs font-semibold text-orange-400">
                                                    Read the comparison
                                                    <ArrowRightIcon className="h-3 w-3 transition-transform group-hover:translate-x-1" />
                                                </div>
                                            </div>
                                        </Link>
                                    </motion.div>
                                );
                            })}
                        </div>
                    </div>
                </section>
            </main>

            <Footer />

            <JsonLd id="comparisons-collection" data={collectionSchema} />
            <JsonLd id="comparisons-breadcrumb" data={breadcrumbSchema} />
            <JsonLd id="comparisons-itemlist" data={itemListSchema} />
        </div>
    );
}
